import { useState } from "react"

import DOMPurify from "dompurify"
import { useTranslation } from "react-i18next"

import type { OpCostUnit, TimeSavedUnit } from "../../types/use-case.types"

import { Pencil } from "@/components/icons"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import { EditorModal } from "./editor-modal"
import { ANN_HOURS } from "../../utils/financial-calculations"
import { formatUSD, formatUSD2 } from "../../utils/format-helpers"

type FactSheetParametersProps = {
  timeSavedHours: string
  timeSavedUnit: TimeSavedUnit
  users: string
  salary: string
  directYear: string
  investment: string
  opCost: string
  opCostUnit: OpCostUnit
  comments: string
  onChange: (field: string, value: string) => void
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), "text/html")
  return doc.body.textContent.trim()
}

export function FactSheetParameters({
  timeSavedHours,
  timeSavedUnit,
  users,
  salary,
  directYear,
  investment,
  opCost,
  opCostUnit,
  comments,
  onChange,
}: FactSheetParametersProps) {
  const { t } = useTranslation()
  const [editorOpen, setEditorOpen] = useState(false)

  const salaryNum = parseFloat(salary) || 70000
  const hourlyRate = salaryNum / ANN_HOURS
  const isEmpty = !stripHtml(comments)
  // Sanitize HTML before rendering to prevent XSS
  const sanitized = DOMPurify.sanitize(comments)

  return (
    <div className="bg-card rounded-lg border p-4 shadow-sm">
      <div className="text-muted-foreground mb-3 text-[10px] font-bold tracking-wider uppercase">
        {t("useCases.factSheet.parameters.title")}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.hoursSaved")}
          </label>
          <div className="flex gap-2">
            <Input
              className="max-w-20"
              min="0"
              step="0.1"
              type="number"
              value={timeSavedHours}
              onChange={(e) => onChange("timeSavedHours", e.target.value)}
            />
            <Select value={timeSavedUnit} onValueChange={(v) => onChange("timeSavedUnit", v)}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{t("useCases.factSheet.parameters.perWeek")}</SelectItem>
                <SelectItem value="month">{t("useCases.factSheet.parameters.perMonth")}</SelectItem>
                <SelectItem value="once">{t("useCases.factSheet.parameters.oneTime")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.activeUsers")}
          </label>
          <Input
            min="0"
            type="number"
            value={users}
            onChange={(e) => onChange("users", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.avgSalary")}
          </label>
          <Input
            min="0"
            step="1000"
            type="number"
            value={salary}
            onChange={(e) => onChange("salary", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.directSavings")}
          </label>
          <Input
            min="0"
            step="500"
            type="number"
            value={directYear}
            onChange={(e) => onChange("directYear", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.investment")}
          </label>
          <Input
            min="0"
            step="100"
            type="number"
            value={investment}
            onChange={(e) => onChange("investment", e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-muted-foreground text-xs font-semibold">
            {t("useCases.factSheet.parameters.opCost")}
          </label>
          <div className="flex gap-2">
            <Input
              min="0"
              step="100"
              type="number"
              value={opCost}
              onChange={(e) => onChange("opCost", e.target.value)}
            />
            <Select value={opCostUnit} onValueChange={(v) => onChange("opCostUnit", v)}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">
                  {t("useCases.factSheet.parameters.monthly")}
                </SelectItem>
                <SelectItem value="yearly">{t("useCases.factSheet.parameters.yearly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="text-muted-foreground mt-3 text-center text-[10px]">
        Working days/year: <b className="text-foreground">220</b> · Hours/day:{" "}
        <b className="text-foreground">8</b> · Working weeks/year:{" "}
        <b className="text-foreground">48</b> · Avg salary:{" "}
        <b className="text-foreground">{formatUSD(salaryNum)}</b>/yr (~
        <b className="text-foreground">{formatUSD2(hourlyRate)}</b>/h)
      </div>

      <div className="mt-3">
        <label className="text-muted-foreground mb-1 block text-xs font-semibold">
          {t("useCases.factSheet.parameters.comments")}
        </label>
        <button
          type="button"
          className={cn(
            "group/preview bg-background/60 hover:bg-background dark:bg-foreground/5 dark:hover:bg-foreground/10 relative w-full cursor-pointer rounded-md border px-3 py-2 text-left transition-colors"
          )}
          onClick={() => setEditorOpen(true)}
        >
          {isEmpty ? (
            <p className="text-muted-foreground text-xs italic">
              {t("useCases.factSheet.parameters.commentsPlaceholder")}
            </p>
          ) : (
            <div
              className="prose dark:prose-invert prose-sm line-clamp-3 max-w-none text-xs leading-relaxed"
              // Content is sanitized with DOMPurify above
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
          )}
          <span className="absolute top-2 right-2 opacity-0 transition-opacity group-hover/preview:opacity-100">
            <Pencil className="text-muted-foreground size-3.5" />
          </span>
        </button>
      </div>

      <EditorModal
        content={comments}
        open={editorOpen}
        placeholder={t("useCases.factSheet.parameters.commentsPlaceholder")}
        title={t("useCases.factSheet.parameters.comments")}
        onOpenChange={setEditorOpen}
        onSave={(html) => onChange("comments", html)}
      />
    </div>
  )
}
